import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Icon from '@components/atoms/icon/icon';
import FloatingCartProduct from '@components/atoms/cart/floating-cart/floating-cart-product-list/components/floating-cart-product';
import { CartContext } from '@context/cart.context';
import { FloatingCartPrice } from '@components/atoms/cart/floating-cart/floating-cart-price/floating-cart-price';
import clsx from 'clsx';
import { useContextSelector } from 'use-context-selector';
import { OrderLine } from '@interfaces/models/orderLine';
import { useBucket } from '@context/bucket.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { FocusTrap } from 'focus-trap-react';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import styles from './floating-cart.module.scss';

const FloatingCart: React.FC = () => {
  const { t } = useTranslation('default-layout');
  const cart = useContextSelector(CartContext, (v) => v.cart);
  const isCartProcessing = useContextSelector(CartContext, (v) => v.isCartProcessing);
  const cartUrl = useContextSelector(CartContext, (v) => v.cartUrl);
  const checkoutUrl = useContextSelector(CartContext, (v) => v.checkoutUrl);
  const { isFeatureEnabled } = useBucket();
  const isCheckoutOnePage = isFeatureEnabled((f) => f.smoothCheckoutOnePagerStructure, false);
  const { sendAnalyticEvent: sendNavigationAnalyticEvent } = useAnalyticEvents('navigation');

  const [loadingCheckout, setLoadingCheckout] = useState<boolean>(true);

  const orderLines: OrderLine[] = cart?.orderLines ?? [];

  const launchLoader = (): void => {
    setLoadingCheckout(true);
  };

  const sendCTAEvent = (cta: 'proceed_to_checkout' | 'view_bag') => {
    sendNavigationAnalyticEvent('navigation_cart', {
      label: cta,
    });
  };

  useEffect(() => {
    sessionStorage.setItem('isCheckoutOnePage', (!!isCheckoutOnePage).toString());
  }, [isCheckoutOnePage]);

  return (
    <ConditionalWrapper
      condition={orderLines.length > 0}
      wrap={(children) => <FocusTrap focusTrapOptions={{ allowOutsideClick: true }}>{children}</FocusTrap>}
    >
      <div className={clsx(styles.cartPreview, { hasLoader: isCartProcessing })}>
        {cart?.orderLines?.length > 0 ? (
          <>
            <ul>
              {orderLines.map((orderLine: OrderLine) => (
                <FloatingCartProduct
                  key={orderLine?.product?.id}
                  orderLine={orderLine}
                  product={orderLine?.product}
                />
              ))}
            </ul>
            <FloatingCartPrice currentCart={cart} />
            <div className={styles.cartPreview__cta}>
              {/* TODO: Click on proceed to checkout and comeback using browser back
              button url changes as [http://localhost:3000/products/8823816/#/products/12487319] */}
              {/* The following link must remain a normal <a>, because it's not part of this project */}
              <a
                data-cy="proceed_to_checkout_in_basket"
                data-vc-dd-action-name="proceed_to_checkout_in_basket"
                className={clsx('vc-btn vc-btn--primary vc-btn--large vc-btn--full', {
                  hasLoading: loadingCheckout,
                })}
                href={isCheckoutOnePage ? checkoutUrl : cartUrl}
                onClick={() => {
                  sendCTAEvent('proceed_to_checkout');
                  launchLoader();
                }}
              >
                {t('HEADER.FLOATING_CART.SEE_ORDER.LABEL')}
              </a>
              {/* The following link must remain a normal <a>, because it's not part of this project */}
              <a
                tabIndex={0}
                href={isCheckoutOnePage ? checkoutUrl : cartUrl}
                className={styles.cartPreview__seeMyBag}
                data-vc-dd-action-name="view_my_shoppingbag_in_basket"
                data-cy="view_my_shoppingbag_in_basket"
                onClick={() => sendCTAEvent('view_bag')}
              >
                {t('HEADER.FLOATING_CART.SEE_CART.LABEL')}
              </a>
            </div>
          </>
        ) : (
          <div className={styles.cartPreview__empty}>
            <Icon
              className={styles.cartPreview__empty__icon}
              name="bag"
              ariaHidden
            />
            <span>{t('HEADER.FLOATING_CART.EMPTY_CART')}</span>
          </div>
        )}
      </div>
    </ConditionalWrapper>
  );
};

export default FloatingCart;
